import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "py-4 sm:py-8 sm:p-8" }
const _hoisted_2 = { class: "mb-10 flex items-center justify-between" }
const _hoisted_3 = {
  key: 0,
  class: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
}
const _hoisted_4 = {
  key: 1,
  class: "bg-white/5 rounded-lg p-10 text-center"
}
const _hoisted_5 = { class: "mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-white/5 to-white/10 flex items-center justify-center mb-4" }
const _hoisted_6 = {
  key: 2,
  class: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
}
const _hoisted_7 = { class: "mt-6" }
const _hoisted_8 = { class: "grid gap-6 py-4" }
const _hoisted_9 = { class: "grid gap-2" }
const _hoisted_10 = { class: "grid gap-2" }
const _hoisted_11 = { class: "grid gap-2" }
const _hoisted_12 = { class: "flex items-center gap-2" }
const _hoisted_13 = { class: "grid gap-2" }
const _hoisted_14 = { class: "flex items-center gap-2" }

import { ref, watch } from 'vue'
import {
  PlusIcon,
} from '@heroicons/vue/24/outline'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { CircleHelp, GitBranch } from 'lucide-vue-next'
import RegisterButton from '@/components/ui/register-button/RegisterButton.vue'
import { useToast } from '@/components/ui/toast'
import { Loader2 } from 'lucide-vue-next'
import DeleteConfirmationDialog from '@/components/ui/delete-dialog/DeleteConfirmationDialog.vue'
import PageTitle from '@/components/ui/page/PageTitle.vue'
import PageDescription from '@/components/ui/page/PageDescription.vue'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { CreateFlowDTO, flows, GetAllFlowsParams, useFlow } from '@/composables/chatFlow/useFlow'
import { Pagination } from '@/interfaces/Pagination'
import { Flow } from '@/interfaces/chat-flow/flow/Flow'
import { FlowStatusEnum, FlowStatusLabels } from '@/enums/chatFlow/flows/FlowStatusEnum'
import { useLeadStartParameters } from '@/composables/useLeadStartParameters'
import { LeadStartParameter } from '@/interfaces/leads/LeadStartParam'
import FlowCard from './components/FlowCard.vue'

// State

export default /*@__PURE__*/_defineComponent({
  __name: 'ManageFlowsView',
  setup(__props) {

const { getAll, create, update, destroy } = useFlow()
const { getAll: getAllLeadStartParameters } = useLeadStartParameters()
const leadStartParameters = ref<Pagination<LeadStartParameter>>()
const perPage = ref('12')
const isFlowDialogOpen = ref(false)
const isDeleteFlowDialogOpen = ref(false)
const isSaving = ref(false)
const isDeletingFlow = ref(false)
const editingFlow = ref<number | null>(null)
const deletingFlow = ref<number | null>(null)
const flowForm = ref<Partial<CreateFlowDTO>>({})
const { toast } = useToast()
const isSwitchConfirmationDialogOpen = ref(false)
const flowToSwitch = ref<Flow | null>(null)

const openNewFlowDialog = () => {
  editingFlow.value = null
  flowForm.value = { 
    status: FlowStatusEnum.ACTIVE,
  }
  isFlowDialogOpen.value = true
}

const closeFlowDialog = () => {
  isFlowDialogOpen.value = false
  flowForm.value = {}
}

const editFlow = (flow: Flow) => {
  editingFlow.value = flow.id
  flowForm.value = { 
    name: flow.name,
    description: flow.description,
    status: flow.status,
    allowChatResume: flow.allowChatResume, 
    leadStartParameterContent: flow.leadStartParameter ? flow.leadStartParameter?.content : null
  } as unknown as Partial<CreateFlowDTO>
  isFlowDialogOpen.value = true
}

const saveFlow = async () => {
  isSaving.value = true

  if (!editingFlow.value) {
      await create({ ...flowForm.value as CreateFlowDTO })
      
      flows.value = await getAll()
      
      toast({
        title: 'Fluxo criado com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
    } else {
      console.log(editingFlow.value, { ...flowForm.value })
      await update(editingFlow.value, { ...flowForm.value })
      const index = flows.value?.data.findIndex(flow => flow.id === editingFlow.value)
      if (index !== undefined && index !== -1 && flows.value) {
        flows.value.data[index] = {
          ...flows.value.data[index],
          name: flowForm.value.name || flows.value.data[index].name,
          description: flowForm.value.description || flows.value.data[index].description,
          status: flowForm.value.status || flows.value.data[index].status,
        }
      }
      
      toast({
        title: 'Fluxo atualizado com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
    }
    
    isSaving.value = false
    closeFlowDialog()
}

const deleteFlow = (flow: Flow) => {
  deletingFlow.value = flow.id
  isDeleteFlowDialogOpen.value = true
}

const confirmDeleteFlow = async () => {
  isDeletingFlow.value = true
  
  await destroy(deletingFlow.value!)
  
  if (flows.value && deletingFlow.value) {
    const index = flows.value.data.findIndex(flow => flow.id === deletingFlow.value)
    if (index !== -1) {
      flows.value.data.splice(index, 1)
    }
  }
  
  toast({
    title: 'Fluxo excluído com sucesso',
    class: 'bg-green-500 text-white',
    duration: 1500
  })
  
  isDeletingFlow.value = false
  isDeleteFlowDialogOpen.value = false
}

const reloadAndGetAll = (params: GetAllFlowsParams) => {
  // Simulate API call to get flows with pagination
  fetchFlows(params)
}

// Mock API
const fetchFlows = (params?: GetAllFlowsParams) => {
  flows.value = undefined
  
  getAll(params).then(response => {
    flows.value = response
  })
}

const handleStatusSwitch = async (flow: Flow) => {
  if (flow.status === FlowStatusEnum.ACTIVE) {
    // Se está tentando desativar, apenas atualize sem confirmação
    await updateFlowStatus(flow.id, FlowStatusEnum.INACTIVE)
  } else {
    // Se está tentando ativar, mostre o modal de confirmação
    flowToSwitch.value = flow
    isSwitchConfirmationDialogOpen.value = true
  }
}

const updateFlowStatus = async (flowId: number, status: FlowStatusEnum) => {
  try {
    await update(flowId, { status })
    
    // Atualize o flow localmente
    if (flows.value) {
      const index = flows.value.data.findIndex(f => f.id === flowId)
      if (index !== -1) {
        flows.value.data[index].status = status
      }
    }
    
    toast({
      title: 'Status atualizado com sucesso',
      class: 'bg-green-500 text-white',
      duration: 1500
    })
  } catch (error) {
    toast({
      title: 'Erro ao atualizar status',
      description: 'Ocorreu um erro ao atualizar o status do fluxo.',
      class: 'bg-red-500 text-white',
      duration: 1500
    })
  }
}

const confirmSwitchFlow = async () => {
  if (!flowToSwitch.value) return
  
  try {
    // Desative todos os outros flows ativos
    if (flows.value) {
      const activeFlows = flows.value.data.filter(f => f.status === FlowStatusEnum.ACTIVE)
      for (const flow of activeFlows) {
        await update(flow.id, { status: FlowStatusEnum.INACTIVE })
      }
    }
    
    // Ative o flow selecionado
    await updateFlowStatus(flowToSwitch.value.id, FlowStatusEnum.ACTIVE)
  } catch (error) {
    toast({
      title: 'Erro ao atualizar fluxos',
      description: 'Ocorreu um erro ao tentar ativar este fluxo e desativar os outros.',
      class: 'bg-red-500 text-white',
      duration: 1500
    })
  } finally {
    isSwitchConfirmationDialogOpen.value = false
    flowToSwitch.value = null
  }
}

// Watchers
watch([isFlowDialogOpen], () => {
  if (isFlowDialogOpen.value) {
    getAllLeadStartParameters({ perPage: '50' }).then(response => {
      leadStartParameters.value = response
    })
  }
})

watch(() => perPage.value, () => {
  reloadAndGetAll({ page: '1', perPage: perPage.value })
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("main", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", null, [
          _createVNode(PageTitle, null, {
            default: _withCtx(() => _cache[11] || (_cache[11] = [
              _createTextVNode(" Gerencie seus fluxos de chat ")
            ])),
            _: 1
          }),
          _createVNode(PageDescription, null, {
            default: _withCtx(() => _cache[12] || (_cache[12] = [
              _createTextVNode(" Crie, personalize e administre seus fluxos de conversação com facilidade. ")
            ])),
            _: 1
          })
        ]),
        _createVNode(RegisterButton, { onClick: openNewFlowDialog }, {
          default: _withCtx(() => _cache[13] || (_cache[13] = [
            _createTextVNode("Criar fluxo")
          ])),
          _: 1
        })
      ]),
      _cache[18] || (_cache[18] = _createElementVNode("div", { class: "mb-6 flex flex-col sm:flex-row gap-4 items-center justify-between" }, null, -1)),
      (_unref(flows) && _unref(flows).data.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(flows).data, (flow) => {
              return (_openBlock(), _createBlock(FlowCard, {
                key: flow.id,
                flow: flow,
                onEditFlow: editFlow,
                onDeleteFlow: deleteFlow,
                onSwitchFlow: handleStatusSwitch
              }, null, 8, ["flow"]))
            }), 128))
          ]))
        : (_unref(flows) && _unref(flows).data.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_unref(GitBranch), { class: "h-8 w-8 text-teal-400" })
              ]),
              _cache[15] || (_cache[15] = _createElementVNode("h3", { class: "text-xl font-semibold text-white mb-2" }, "Nenhum fluxo encontrado", -1)),
              _cache[16] || (_cache[16] = _createElementVNode("p", { class: "text-gray-400 mb-6 max-w-md mx-auto" }, "Comece criando seu primeiro fluxo de chat para interagir com seus usuários.", -1)),
              _createVNode(_unref(Button), { onClick: openNewFlowDialog }, {
                default: _withCtx(() => [
                  _createVNode(_unref(PlusIcon), { class: "h-4 w-4 mr-2" }),
                  _cache[14] || (_cache[14] = _createTextVNode(" Criar meu primeiro fluxo "))
                ]),
                _: 1
              })
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_6, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(8, (i) => {
                return _createElementVNode("div", {
                  key: i,
                  class: "bg-gray-800 skeleton-shine rounded-lg overflow-hidden"
                }, _cache[17] || (_cache[17] = [
                  _createStaticVNode("<div class=\"h-60 bg-zinc-700/50\"></div><div class=\"p-4\"><div class=\"h-6 bg-zinc-700/50 rounded w-3/4 mb-3\"></div><div class=\"h-4 bg-zinc-700/50 rounded w-full mb-2\"></div><div class=\"h-4 bg-zinc-700/50 rounded w-2/3\"></div><div class=\"flex justify-between mt-4\"><div class=\"h-3 bg-zinc-700/50 rounded w-1/4\"></div><div class=\"h-3 bg-zinc-700/50 rounded w-1/4\"></div></div></div><div class=\"p-4 pt-0 flex justify-between\"><div class=\"h-4 bg-zinc-700/50 rounded w-1/4\"></div><div class=\"h-4 bg-zinc-700/50 rounded w-1/4\"></div></div>", 3)
                ]))
              }), 64))
            ])),
      _createElementVNode("div", _hoisted_7, [
        (_unref(flows)?.pagination)
          ? (_openBlock(), _createBlock(_unref(Pagination), {
              key: 0,
              data: _unref(flows).pagination,
              "per-page": perPage.value,
              "onUpdate:perPage": _cache[0] || (_cache[0] = ($event: any) => ((perPage).value = $event)),
              "onUpdate:page": _cache[1] || (_cache[1] = (page) => reloadAndGetAll({ page, perPage: perPage.value }))
            }, null, 8, ["data", "per-page"]))
          : _createCommentVNode("", true)
      ])
    ]),
    _createVNode(_unref(Dialog), {
      open: isFlowDialogOpen.value,
      "onUpdate:open": _cache[7] || (_cache[7] = ($event: any) => ((isFlowDialogOpen).value = $event)),
      onClose: closeFlowDialog
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(DialogContent), { class: "sm:max-w-xl" }, {
          default: _withCtx(() => [
            _createVNode(_unref(DialogHeader), null, {
              default: _withCtx(() => [
                _createVNode(_unref(DialogTitle), null, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(editingFlow.value ? 'Editar fluxo' : 'Novo fluxo de chat'), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DialogDescription), null, {
                  default: _withCtx(() => _cache[19] || (_cache[19] = [
                    _createTextVNode(" Configure as informações do seu fluxo de conversação. ")
                  ])),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("div", _hoisted_9, [
                _createVNode(_unref(Label), null, {
                  default: _withCtx(() => _cache[20] || (_cache[20] = [
                    _createTextVNode("Nome do fluxo "),
                    _createElementVNode("span", { class: "text-red-500" }, "*", -1)
                  ])),
                  _: 1
                }),
                _createVNode(_unref(Input), {
                  modelValue: flowForm.value.name,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((flowForm.value.name) = $event)),
                  placeholder: "Ex: Atendimento ao cliente"
                }, null, 8, ["modelValue"])
              ]),
              _createElementVNode("div", _hoisted_10, [
                _createVNode(_unref(Label), null, {
                  default: _withCtx(() => _cache[21] || (_cache[21] = [
                    _createTextVNode("Descrição")
                  ])),
                  _: 1
                }),
                _createVNode(_unref(Textarea), {
                  modelValue: flowForm.value.description,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((flowForm.value.description) = $event)),
                  placeholder: "Descreva o propósito deste fluxo de chat...",
                  class: "resize-none",
                  rows: "3"
                }, null, 8, ["modelValue"])
              ]),
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("div", _hoisted_12, [
                  _createVNode(_unref(Label), null, {
                    default: _withCtx(() => _cache[22] || (_cache[22] = [
                      _createTextVNode("Status "),
                      _createElementVNode("span", { class: "text-red-500" }, "*", -1)
                    ])),
                    _: 1
                  }),
                  _createVNode(_unref(TooltipProvider), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Tooltip), { "delay-duration": 100 }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(TooltipTrigger), { "as-child": "" }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(CircleHelp), { class: "size-5 text-primary" })
                            ]),
                            _: 1
                          }),
                          _createVNode(_unref(TooltipContent), {
                            side: "top",
                            "side-offset": 5
                          }, {
                            default: _withCtx(() => _cache[23] || (_cache[23] = [
                              _createTextVNode(" Status inativo: Este fluxo não será "),
                              _createElementVNode("br", null, null, -1),
                              _createTextVNode(" disponibilizado aos usuários. ")
                            ])),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _createVNode(_unref(Select), {
                  modelValue: flowForm.value.status,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((flowForm.value.status) = $event))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(SelectTrigger), null, {
                      default: _withCtx(() => [
                        _createVNode(_unref(SelectValue), { placeholder: "Selecione o status" })
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(SelectContent), null, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(FlowStatusEnum), (status) => {
                          return (_openBlock(), _createBlock(_unref(SelectItem), { value: status }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_unref(FlowStatusLabels)[status]), 1)
                            ]),
                            _: 2
                          }, 1032, ["value"]))
                        }), 256))
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _createElementVNode("div", _hoisted_13, [
                _createVNode(_unref(Label), null, {
                  default: _withCtx(() => _cache[24] || (_cache[24] = [
                    _createTextVNode("Palavra-chave "),
                    _createElementVNode("span", { class: "text-xs text-zinc-400" }, "(opcional)", -1)
                  ])),
                  _: 1
                }),
                _createVNode(_unref(Input), {
                  modelValue: flowForm.value.leadStartParameterContent,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((flowForm.value.leadStartParameterContent) = $event)),
                  placeholder: "Ex: campaign-1"
                }, null, 8, ["modelValue"])
              ]),
              _createElementVNode("div", _hoisted_14, [
                _createVNode(_unref(Switch), {
                  checked: flowForm.value.allowChatResume,
                  "onUpdate:checked": _cache[6] || (_cache[6] = ($event: any) => ((flowForm.value.allowChatResume) = $event))
                }, null, 8, ["checked"]),
                _createVNode(_unref(Label), null, {
                  default: _withCtx(() => _cache[25] || (_cache[25] = [
                    _createTextVNode("Permitir retomar conversa")
                  ])),
                  _: 1
                }),
                _createVNode(_unref(TooltipProvider), null, {
                  default: _withCtx(() => [
                    _createVNode(_unref(Tooltip), { "delay-duration": 100 }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(TooltipTrigger), { "as-child": "" }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(CircleHelp), { class: "size-5 text-primary" })
                          ]),
                          _: 1
                        }),
                        _createVNode(_unref(TooltipContent), {
                          side: "top",
                          "side-offset": 5
                        }, {
                          default: _withCtx(() => _cache[26] || (_cache[26] = [
                            _createTextVNode(" Quando ativado, os usuários podem continuar de onde pararam ao iniciarem novamente. ")
                          ])),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ])
            ]),
            _createVNode(_unref(DialogFooter), null, {
              default: _withCtx(() => [
                _createVNode(_unref(Button), {
                  class: "text-white",
                  variant: "ghost",
                  onClick: closeFlowDialog
                }, {
                  default: _withCtx(() => _cache[27] || (_cache[27] = [
                    _createTextVNode("Cancelar")
                  ])),
                  _: 1
                }),
                _createVNode(_unref(Button), {
                  disabled: isSaving.value || !flowForm.value.name,
                  onClick: saveFlow
                }, {
                  default: _withCtx(() => [
                    (isSaving.value)
                      ? (_openBlock(), _createBlock(_unref(Loader2), {
                          key: 0,
                          class: "w-4 h-4 animate-spin mr-2"
                        }))
                      : _createCommentVNode("", true),
                    _createTextVNode(" " + _toDisplayString(editingFlow.value ? 'Salvar alterações' : 'Criar fluxo'), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["open"]),
    _createVNode(DeleteConfirmationDialog, {
      open: isDeleteFlowDialogOpen.value,
      "onUpdate:open": _cache[8] || (_cache[8] = ($event: any) => ((isDeleteFlowDialogOpen).value = $event)),
      loading: isDeletingFlow.value,
      title: "Excluir fluxo de chat",
      description: "Tem certeza que deseja excluir este fluxo? Esta ação não pode ser desfeita e todos os dados associados serão perdidos.",
      onConfirm: confirmDeleteFlow
    }, null, 8, ["open", "loading"]),
    _createVNode(_unref(Dialog), {
      open: isSwitchConfirmationDialogOpen.value,
      "onUpdate:open": _cache[10] || (_cache[10] = ($event: any) => ((isSwitchConfirmationDialogOpen).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(DialogContent), { class: "sm:max-w-md" }, {
          default: _withCtx(() => [
            _createVNode(_unref(DialogHeader), null, {
              default: _withCtx(() => [
                _createVNode(_unref(DialogTitle), null, {
                  default: _withCtx(() => _cache[28] || (_cache[28] = [
                    _createTextVNode("Ativar este fluxo?")
                  ])),
                  _: 1
                }),
                _createVNode(_unref(DialogDescription), null, {
                  default: _withCtx(() => _cache[29] || (_cache[29] = [
                    _createTextVNode(" Ao ativar este fluxo, todos os outros fluxos ativos serão automaticamente desativados. Você tem certeza que deseja continuar? ")
                  ])),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(DialogFooter), { class: "gap-2" }, {
              default: _withCtx(() => [
                _createVNode(_unref(Button), {
                  variant: "outline",
                  class: "text-white",
                  onClick: _cache[9] || (_cache[9] = ($event: any) => (isSwitchConfirmationDialogOpen.value = false))
                }, {
                  default: _withCtx(() => _cache[30] || (_cache[30] = [
                    _createTextVNode(" Cancelar ")
                  ])),
                  _: 1
                }),
                _createVNode(_unref(Button), { onClick: confirmSwitchFlow }, {
                  default: _withCtx(() => [
                    (isSaving.value)
                      ? (_openBlock(), _createBlock(_unref(Loader2), {
                          key: 0,
                          class: "w-4 h-4 animate-spin mr-2"
                        }))
                      : _createCommentVNode("", true),
                    _cache[31] || (_cache[31] = _createTextVNode(" Confirmar "))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["open"])
  ], 64))
}
}

})